import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiEye, FiPrinter, FiMessageCircle, FiTrash2, FiFile, FiFileText, FiSettings } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PinModal from '../components/PinModal';
import { printInvoice } from '../utils/invoicePrinter';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const Invoices = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [printFormat, setPrintFormat] = useState('A4'); // Format d'impression par défaut

  const isAdmin = user?.role === 'admin';
  const canView = user?.role === 'admin' || user?.role === 'caissier';
  const canDelete = isAdmin;

  useEffect(() => { if (canView) fetchInvoices(); }, []);
  useEffect(() => { filterInvoices(); }, [startDate, endDate, statusFilter, invoices]);

  const fetchInvoices = async () => {
    try {
      const response = await api.get('/sales');
      setInvoices(response.data);
      setFilteredInvoices(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  };

  const filterInvoices = () => {
    let filtered = [...invoices];
    if (startDate) filtered = filtered.filter(inv => new Date(inv.created_at) >= startDate);
    if (endDate) filtered = filtered.filter(inv => new Date(inv.created_at) <= endDate);
    if (statusFilter) filtered = filtered.filter(inv => inv.status === statusFilter);
    setFilteredInvoices(filtered);
  };

  const viewDetails = async (invoiceId) => {
    try {
      const response = await api.get(`/sales/${invoiceId}`);
      setSelectedInvoice(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      await api.delete(`/sales/${invoiceToDelete.id}`);
      toast.success('Facture supprimée avec succès');
      setShowPinModal(false);
      setInvoiceToDelete(null);
      fetchInvoices();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Impression avec format choisi
  const handlePrint = async (invoice) => {
    try {
      const response = await api.get(`/sales/${invoice.id}`);
      const fullInvoice = response.data;
      // Passer le format choisi à la fonction d'impression
      printInvoice(fullInvoice, printFormat);
    } catch (error) {
      toast.error('Erreur lors de l\'impression');
    }
  };

  const handleWhatsApp = (invoice) => {
    const message = `*MST - FACTURE*\n\nN°: ${invoice.invoice_number}\nClient: ${invoice.client_name}\nTotal: ${invoice.total_amount.toLocaleString()} FCFA\nPayé: ${invoice.paid_amount.toLocaleString()} FCFA\n\nMerci de votre visite !`;
    const phone = invoice.client_phone?.replace(/[^0-9]/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
    } else {
      toast.error('Numéro de téléphone non disponible');
    }
  };

  // Export Excel
  const exportToExcel = () => {
    const exportData = filteredInvoices.map(inv => ({
      'N° Facture': inv.invoice_number,
      'Client': inv.client_name || 'Anonyme',
      'Date': new Date(inv.created_at).toLocaleDateString('fr-FR'),
      'Total (FCFA)': inv.total_amount,
      'Payé (FCFA)': inv.paid_amount,
      'Reste (FCFA)': inv.total_amount - inv.paid_amount,
      'Statut': inv.status === 'paid' ? 'Payé' : inv.status === 'partial' ? 'Partiel' : 'En attente'
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Factures');
    XLSX.writeFile(wb, `factures_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Export Excel réussi');
  };

  // Export PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFillColor(16, 185, 129);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('MULTI SERVICE TECHNOLOGIES', pageWidth / 2, 20, { align: 'center' });
    doc.setFontSize(10);
    doc.text('Liste des factures', pageWidth / 2, 32, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    const totalAmount = filteredInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
    doc.text(`Total factures: ${filteredInvoices.length} | Montant: ${totalAmount.toLocaleString()} FCFA`, 14, 55);
    const tableHeaders = [['Facture', 'Client', 'Date', 'Total', 'Statut']];
    const tableRows = filteredInvoices.slice(0, 50).map(inv => [
      inv.invoice_number,
      inv.client_name || '-',
      new Date(inv.created_at).toLocaleDateString('fr-FR'),
      `${inv.total_amount.toLocaleString()} FCFA`,
      inv.status === 'paid' ? 'Payé' : inv.status === 'partial' ? 'Partiel' : 'Attente'
    ]);
    doc.autoTable({
      startY: 65,
      head: tableHeaders,
      body: tableRows,
      theme: 'striped',
      styles: { fontSize: 8 },
      headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] }
    });
    doc.save(`factures_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('Export PDF réussi');
  };

  const formatAmount = (amount) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);
  const formatDate = (date) => new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  
  const getStatusBadge = (status) => {
    const colors = { paid: 'bg-green-100 text-green-800', partial: 'bg-orange-100 text-orange-800', pending: 'bg-red-100 text-red-800' };
    const labels = { paid: 'Payé', partial: 'Partiel', pending: 'En attente' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>{labels[status] || status}</span>;
  };

  if (!canView) return null;
  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Factures</h1>
        <div className="flex flex-wrap items-center gap-3">
          {/* Sélecteur de format d'impression */}
          <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200">
            <FiSettings className="text-gray-500" size={16} />
            <label className="text-sm font-medium text-gray-700">Format:</label>
            <select
              value={printFormat}
              onChange={(e) => setPrintFormat(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="A4">A4 (210 x 297 mm)</option>
              <option value="A5">A5 (148 x 210 mm)</option>
            </select>
          </div>
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700 transition-colors">
            <FiFile size={18} /> Excel
          </button>
          <button onClick={exportToPDF} className="bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-red-700 transition-colors">
            <FiFileText size={18} /> PDF
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DatePicker placeholderText="Date début" selected={startDate} onChange={setStartDate} className="input-field" dateFormat="dd/MM/yyyy" isClearable />
          <DatePicker placeholderText="Date fin" selected={endDate} onChange={setEndDate} className="input-field" dateFormat="dd/MM/yyyy" isClearable />
          <select className="input-field" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="paid">Payé</option>
            <option value="partial">Partiel</option>
            <option value="pending">En attente</option>
          </select>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead className="border-b">
            <tr className="text-left">
              <th className="pb-3">N° Facture</th>
              <th className="pb-3">Client</th>
              <th className="pb-3">Date</th>
              <th className="pb-3 text-right">Total</th>
              <th className="pb-3 text-right">Payé</th>
              <th className="pb-3 text-right">Reste</th>
              <th className="pb-3 text-center">Statut</th>
              <th className="pb-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => {
              const remaining = invoice.total_amount - invoice.paid_amount;
              return (
                <tr key={invoice.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-3 font-medium">{invoice.invoice_number}</td>
                  <td className="py-3">{invoice.client_name || 'Anonyme'}</td>
                  <td className="py-3 text-sm">{formatDate(invoice.created_at)}</td>
                  <td className="py-3 text-right text-green-600 font-medium">{formatAmount(invoice.total_amount)}</td>
                  <td className="py-3 text-right">{formatAmount(invoice.paid_amount)}</td>
                  <td className="py-3 text-right font-medium text-red-600">{formatAmount(remaining)}</td>
                  <td className="py-3 text-center">{getStatusBadge(invoice.status)}</td>
                  <td className="py-3 text-center">
                    <div className="flex justify-center gap-2 flex-wrap">
                      <button onClick={() => viewDetails(invoice.id)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Détails">
                        <FiEye size={16} />
                      </button>
                      <button onClick={() => handlePrint(invoice)} className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg" title="Imprimer">
                        <FiPrinter size={16} />
                      </button>
                      <button onClick={() => handleWhatsApp(invoice)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="WhatsApp">
                        <FiMessageCircle size={16} />
                      </button>
                      {canDelete && (
                        <button onClick={() => { setInvoiceToDelete(invoice); setShowPinModal(true); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Supprimer">
                          <FiTrash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredInvoices.length === 0 && <div className="text-center py-8"><p className="text-gray-500">Aucune facture trouvée</p></div>}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Facture {selectedInvoice.invoice_number}</h2>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">✕</button>
            </div>
            
            <div className="mb-4 p-4 bg-green-50 rounded-lg">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm text-gray-500">Client</p><p className="font-semibold">{selectedInvoice.client_name || 'Anonyme'}</p></div>
                <div><p className="text-sm text-gray-500">Téléphone</p><p className="font-semibold">{selectedInvoice.client_phone || 'N/A'}</p></div>
                <div><p className="text-sm text-gray-500">Date</p><p className="font-semibold">{new Date(selectedInvoice.created_at).toLocaleString('fr-FR')}</p></div>
                <div><p className="text-sm text-gray-500">Statut</p>{getStatusBadge(selectedInvoice.status)}</div>
              </div>
            </div>
            
            <h3 className="font-semibold mb-2">Articles</h3>
            <div className="overflow-x-auto">
              <table className="w-full mb-4">
                <thead className="border-b bg-gray-50">
                  <tr><th className="py-2 text-left">Produit</th><th className="py-2 text-center">Qté</th><th className="py-2 text-right">Prix unit.</th><th className="py-2 text-right">Total</th></tr>
                </thead>
                <tbody>
                  {selectedInvoice.items?.map((item, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="py-2">{item.product_name}</td>
                      <td className="py-2 text-center">{item.quantity}</td>
                      <td className="py-2 text-right">{formatAmount(item.unit_price)}</td>
                      <td className="py-2 text-right">{formatAmount(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 text-right space-y-1">
              <p>Sous-total: {formatAmount((selectedInvoice.total_amount || 0) + (selectedInvoice.discount || 0))}</p>
              {(selectedInvoice.discount || 0) > 0 && <p>Remise: -{formatAmount(selectedInvoice.discount)}</p>}
              <p className="text-lg font-bold text-green-600">Total: {formatAmount(selectedInvoice.total_amount)}</p>
              <p>Payé: {formatAmount(selectedInvoice.paid_amount)}</p>
              <p>Mode: {{ cash: 'Espèces', mobile_money: 'Mobile Money', bank_transfer: 'Virement', credit: 'Crédit' }[selectedInvoice.payment_method] || selectedInvoice.payment_method}</p>
              {(selectedInvoice.total_amount - selectedInvoice.paid_amount) > 0 && <p className="text-red-600 font-bold">Reste à payer: {formatAmount(selectedInvoice.total_amount - selectedInvoice.paid_amount)}</p>}
            </div>
            
            <div className="flex gap-3 mt-6">
              <button 
                onClick={() => handlePrint(selectedInvoice)} 
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <FiPrinter size={16} />
                Imprimer ({printFormat})
              </button>
              <button onClick={() => handleWhatsApp(selectedInvoice)} className="flex-1 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2">
                <FiMessageCircle size={16} />
                WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}

      <PinModal 
        isOpen={showPinModal} 
        onClose={() => { setShowPinModal(false); setInvoiceToDelete(null); }} 
        onConfirm={handleDelete} 
        title="Supprimer une facture" 
        message="Cette action est irréversible." 
      />
    </div>
  );
};

export default Invoices;